import {ClientCallType} from '@pexip/infinity/clientCallType';

import {CALL_TYPE} from '../constants';

import {getClientCallType} from './getClientCallType';
import {isClientCallType} from './isClientCallType';
import {valueToNumber} from './valueToNumber';

/**
 * Converts a call type name or numeric value into a client call type.
 *
 * @param callType - Call type from a link, branding, or configuration.
 * @returns The client call type, or the default call type when the value is
 * unknown.
 *
 * @example
 * toClientCallType('audioonly'); // ClientCallType.AudioSendRecvPresentationSendRecv
 * toClientCallType('unknown'); // CALL_TYPE
 */
export const toClientCallType = (callType: string): ClientCallType => {
    const clientCallType = valueToNumber(callType);
    if (isClientCallType(clientCallType)) {
        return clientCallType;
    }

    const mappedCallType = getClientCallType(callType);
    if (mappedCallType) {
        return mappedCallType;
    }

    switch (callType) {
        case 'none': {
            return ClientCallType.None;
        }
        case 'presentation-only': {
            return ClientCallType.PresentationSendRecv;
        }
        case 'audioonly': {
            return ClientCallType.AudioSendRecvPresentationSendRecv;
        }
        case 'audiorecvonly': {
            return ClientCallType.AudioRecvPresentationSendRecv;
        }
        case 'audiosendonly': {
            return ClientCallType.AudioSendPresentationSendRecv;
        }
        case 'videoonly': {
            return ClientCallType.VideoSendRecvPresentationSendRecv;
        }
        case 'videorecvonly': {
            return ClientCallType.VideoRecvPresentationSendRecv;
        }
        case 'videosendonly': {
            return ClientCallType.VideoSendPresentationSendRecv;
        }
        case 'audiovideosendonly': {
            return ClientCallType.AudioSendVideoSendPresentationSendRecv;
        }
        case 'audiovideorecvonly': {
            return ClientCallType.AudioRecvVideoRecvPresentationSendRecv;
        }
        case 'video': {
            return ClientCallType.AudioSendRecvVideoSendRecvPresentationSendRecv;
        }
        default: {
            return CALL_TYPE;
        }
    }
};
